import { useEffect, useState } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';

import { useUserQuiz } from '@lib/core/quizzes/hooks';
import { URL_QUIZ_TYPE_TASTE_REMOTE } from '@lib/core/quizzes/utils/consts';
import { useRemoteAccess } from '@lib/core/retailers/hooks/useRemoteAccess';
import { useRetailer } from '@lib/core/retailers/hooks/useRetailer';
import {
  actionResetRemoteAccess,
  actionSetRemoteAccessParams,
  actionUseRemoteAccessKey,
  actionVerifyRemoteAccessKey,
} from '@lib/core/retailers/slices/remoteAccess';
import { stringToBoolean } from '@lib/core/service/utils';
import { RouteUtils } from '@lib/tools/routes';
import { useAddons, useEffectSkipFirst } from '@lib/tools/views/hooks';
import { useTypedDispatch } from '@lib/tools/views/hooks/useTypedDispatch';
import { PAGES } from '@lib/tools/views/urls';

import { AgeGate } from '@components/pmi/src/common/AgeGate';
import { Geoblock } from '@components/pmi/src/common/Geoblock';
import { HealthWarningPrePage } from '@components/pmi/src/common/HealthWarningPrepage';
import { InvalidVerificationLinkGate } from '@components/pmi/src/common/InvalidVerificationLinkGate';
import { AGE_CONFIRMED } from '@components/pmi/src/utils/consts';

export const HealthWarningPrePageAccess = () => {
  const { isHealthWarningPrepageAddon } = useAddons();

  const [showWarning, setShowWarning] = useState(isHealthWarningPrepageAddon);

  const handleClose = () => {
    setShowWarning(false);
  };

  return showWarning ? <HealthWarningPrePage handleClose={handleClose} /> : <Outlet />;
};

export const GeoBlockAccess = () => {
  const { retailerError } = useRetailer();

  return retailerError ? <Geoblock /> : <Outlet />;
};

export const RemoteAccess = () => {
  const dispatch = useTypedDispatch();
  const { remoteAccessKey, isRemoteAccessValid } = useRemoteAccess();
  const { userQuizId } = useUserQuiz();
  const { quizType: urlQuizType } = useParams();

  const remoteGtRoutes = [PAGES.ta.landingRemote, PAGES.ta.quiz.remote];
  const isVerificationRequired = remoteAccessKey && remoteGtRoutes.some(route => RouteUtils.currentPageContains(route));
  const isOpenIntegrationPage = RouteUtils.currentPageContains(PAGES.ta.openIntegration);

  useEffect(() => {
    const { accessKey, expiry, retries, customId } = RouteUtils.getRemoteAccessParams();

    if (accessKey !== remoteAccessKey && !isOpenIntegrationPage) {
      dispatch(actionResetRemoteAccess());
    }

    if (accessKey) {
      dispatch(
        actionSetRemoteAccessParams({
          access_key: accessKey,
          custom_id: customId,
          expiry,
          is_valid: true,
          retries: parseInt(retries, 10),
        }),
      ).then(() => {
        dispatch(actionVerifyRemoteAccessKey());
      });
    }
  }, []);

  /**
   * * Use remote access key on remote quiz route, if valid
   */
  useEffectSkipFirst(() => {
    if (urlQuizType === URL_QUIZ_TYPE_TASTE_REMOTE) {
      if (userQuizId) {
        if (remoteAccessKey && isRemoteAccessValid) {
          dispatch(actionUseRemoteAccessKey());
        }
      } else {
        dispatch(actionVerifyRemoteAccessKey());
      }
    }
  }, [userQuizId]);

  return isRemoteAccessValid || !isVerificationRequired ? <Outlet /> : <InvalidVerificationLinkGate />;
};

export const LegalAgeAccess = () => {
  const { isAgeGateAddon } = useAddons();
  const [currentAgeConfirmedValue, setCurrentAgeConfirmedValue] = useState<boolean>(false);

  useEffect(() => {
    const ageConfirmedFromLocalStorage = stringToBoolean(localStorage.getItem(AGE_CONFIRMED) || '');
    setCurrentAgeConfirmedValue(ageConfirmedFromLocalStorage || false);
  }, []);

  const handleUpdateCurrentAgeConfirmedValue = () => {
    setCurrentAgeConfirmedValue(true);
  };

  return !isAgeGateAddon || currentAgeConfirmedValue ? (
    <Outlet />
  ) : (
    <AgeGate handleUpdateCurrentAgeConfirmedValue={handleUpdateCurrentAgeConfirmedValue} />
  );
};

/**
 * * Patches German fuck-up of incorrectly constructed distributed remote GT links
 * ! Incorrect link: /remotegt/?access_key=xxx&expiry=xxx&retries=500000&custom_id=xxx?utm_cid=xxx
 */
export const PatchRemoteGuidedTrialAccess = () => {
  const { pathname } = window.location;
  const searchParams = window.location.search;

  const splitParams = searchParams.split('?');
  const isLinkInvalid = splitParams.length > 2;

  return isLinkInvalid ? <Navigate replace to={`${pathname}?${splitParams[1]}`} /> : <Outlet />;
};
